import {
  SPOOL_STATUS_LABELS,
  SUPPLY_ADJUST_LABELS,
  SUPPLY_CATEGORY_LABELS,
  SUPPLY_UNIT_LABELS,
  Supply,
  SupplyAdjustMotive,
  SpoolStatus,
  type Filament,
  formatBRL,
  shoppingListText,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { useFilaments, useSupplies } from '../../lib/catalog-api';
import {
  formatG,
  useInvalidateInventory,
  useInventorySummary,
  useSetShopping,
  useShoppingList,
  useSpools,
} from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { useIsPhone } from '../../lib/use-media';
import { Panel, QrScanSheet } from './qr';
import { copyText } from './ShoppingTab';
import { SpoolBadge, Swatch, spoolPlace } from './spool-common';

const ADJUST_HELP: Record<SupplyAdjustMotive, string> = {
  use: 'Saída para um pedido ou peça fora dos kits.',
  loss: 'Estragou, quebrou ou sumiu. Entra como perda nos relatórios.',
  count: 'Você contou na prateleira e o número é outro.',
  other: 'Outro motivo; descreva na nota.',
};

const qty = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

export function AdjustSupplyPanel({ supply, onClose }: { supply: Supply; onClose: () => void }) {
  const [delta, setDelta] = useState(0);
  const [motive, setMotive] = useState<SupplyAdjustMotive>('count');
  const invalidate = useInvalidateInventory();
  const toast = useToast();
  const step = supply.unit === 'unit' && supply.quantityOnHand >= 100 ? 10 : 1;
  const next = supply.quantityOnHand + delta;
  const save = useMutation({
    mutationFn: () => api.post(`/supplies/${supply.id}/adjust`, Supply, { delta, motive }),
    onSuccess: () => {
      invalidate();
      toast(
        `${supply.name}: ${delta > 0 ? '+' : ''}${qty(delta)} (${SUPPLY_ADJUST_LABELS[motive].toLowerCase()}). Salvo.`,
      );
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  return (
    <Panel label={supply.name} onClose={onClose}>
      <p className="sm muted">
        Em estoque: {qty(supply.quantityOnHand)} {SUPPLY_UNIT_LABELS[supply.unit].short}
        {supply.minimumQuantity ? ` · mínimo ${qty(supply.minimumQuantity)}` : ''}
      </p>
      <div className="row" style={{ gap: 12, justifyContent: 'center', alignItems: 'center' }}>
        <button
          type="button"
          className="btn icon"
          aria-label="Menos"
          onClick={() => setDelta(delta - step)}
        >
          −
        </button>
        <span className="fr tab" style={{ fontSize: 28, minWidth: 80, textAlign: 'center' }}>
          {delta > 0 ? '+' : ''}
          {qty(delta)}
        </span>
        <button
          type="button"
          className="btn icon"
          aria-label="Mais"
          onClick={() => setDelta(delta + step)}
        >
          +
        </button>
      </div>
      <p className="sm" style={{ textAlign: 'center', color: next < 0 ? 'var(--err)' : undefined }}>
        fica {qty(next)}
        {supply.minimumQuantity && next < supply.minimumQuantity && next >= 0
          ? ' · abaixo do mínimo'
          : ''}
        {next < 0 ? ' · o estoque não pode ficar negativo' : ''}
      </p>
      <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Motivo">
        {SupplyAdjustMotive.options.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={motive === m}
            className={motive === m ? 'chip on' : 'chip'}
            onClick={() => setMotive(m)}
          >
            {SUPPLY_ADJUST_LABELS[m]}
          </button>
        ))}
      </div>
      <p className="xs muted">{ADJUST_HELP[motive]}</p>
      <button
        type="button"
        className="btn p block"
        disabled={next < 0 || save.isPending}
        onClick={() =>
          delta === 0 ? toast('Nada mudou. Use − ou + para ajustar.') : save.mutate()
        }
      >
        Salvar ajuste
      </button>
    </Panel>
  );
}

function ShoppingPanel({ onClose }: { onClose: () => void }) {
  const list = useShoppingList();
  const toast = useToast();
  const l = list.data;
  return (
    <Panel label="Lista de compras" onClose={onClose}>
      <p className="sm muted">Tudo o que está abaixo do mínimo.</p>
      {!l ? <Skeleton rows={2} height={40} /> : null}
      {l ? (
        <>
          <span className="lbl">Filamentos</span>
          {l.filaments.map((f) => (
            <div key={f.filamentId} className="kv sm">
              <span>
                {f.name} · {f.spools} × {formatBRL(f.pricePerSpoolCentavos)}
              </span>
              <span className="v">{formatBRL(f.subtotalCentavos)}</span>
            </div>
          ))}
          {l.filaments.length === 0 ? <p className="sm muted">Nenhum filamento faltando.</p> : null}
          <span className="lbl">Insumos</span>
          {l.supplies.map((s) => (
            <div key={s.supplyId} className="kv sm">
              <span>
                {s.name} · comprar {qty(s.quantity)} · tem {qty(s.quantityOnHand)}, mínimo{' '}
                {qty(s.minimumQuantity)}
              </span>
              <span className="v">{formatBRL(s.subtotalCentavos)}</span>
            </div>
          ))}
          {l.supplies.length === 0 ? (
            <p className="sm muted">Nenhum insumo abaixo do mínimo.</p>
          ) : null}
          <div className="kv">
            <b>Total estimado</b>
            <b className="v">{formatBRL(l.totalCentavos)}</b>
          </div>
          <button
            type="button"
            className="btn block"
            onClick={() =>
              void copyText(shoppingListText(l)).then((ok) =>
                toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar neste navegador.'),
              )
            }
          >
            Copiar lista
          </button>
          <Link className="btn p block" to="/filamentos/compra">
            Registrar compra
          </Link>
        </>
      ) : null}
    </Panel>
  );
}

/** Estoque-Celular filament sheet: stock, spools and "Adicionar à lista de compras". */
function FilamentSheet({ f, onClose }: { f: Filament; onClose: () => void }) {
  const toast = useToast();
  const spools = useSpools({ filamentId: f.id, active: true });
  const list = useShoppingList();
  const setQty = useSetShopping();
  const inList = list.data?.filaments.find((l) => l.filamentId === f.id)?.spools ?? 0;
  return (
    <Panel label={f.name} onClose={onClose}>
      <div className="row" style={{ gap: 10 }}>
        <Swatch hex={f.colorHex} size={22} />
        <span className="sm muted">
          {f.brand} · {f.material}
        </span>
      </div>
      <p>
        <span className="fr tab" style={{ fontSize: 28 }}>
          {formatG(f.stockGrams)}
        </span>{' '}
        <span className="sm muted">
          {f.minimumGrams ? `mínimo ${formatG(f.minimumGrams)}` : 'sem mínimo'}
        </span>
      </p>
      <div className="col" style={{ gap: 0 }}>
        {(spools.data?.data ?? []).map((s) => (
          <Link key={s.id} to={`/carreteis/${s.id}`} className="listrow">
            <b className="tab">{s.code}</b>
            <span className="grow sm muted">{spoolPlace(s)}</span>
            <span className="tab sm">{formatG(s.remainingGrams)}</span>
          </Link>
        ))}
        {spools.data && !spools.data.data.length ? (
          <p className="sm muted">Nenhum carretel com filamento.</p>
        ) : null}
      </div>
      <p className="sm">
        {inList
          ? `Na lista de compras: ${inList} ${inList === 1 ? 'carretel' : 'carretéis'}.`
          : 'Fora da lista de compras.'}
      </p>
      <button
        type="button"
        className="btn block"
        disabled={setQty.isPending || !list.data}
        onClick={() =>
          setQty.mutate(
            { kind: 'filaments', id: f.id, quantity: inList + 1 },
            {
              onSuccess: () => toast(`${f.name}: +1 carretel na lista de compras.`),
              onError: (err) => toast(errorMessage(err)),
            },
          )
        }
      >
        Adicionar à lista de compras
      </button>
    </Panel>
  );
}

/** Phone "Estoque" (boards Estoque-Celular, Estoque-Celular-Carreteis). Desktop uses the full pages. */
export function StockPhonePage() {
  const phone = useIsPhone();
  const [tab, setTab] = useState<'filamentos' | 'carreteis' | 'insumos'>('filamentos');
  const [openFilament, setOpenFilament] = useState<Filament | null>(null);
  const [status, setStatus] = useState<SpoolStatus | ''>('');
  const [panel, setPanel] = useState<'shopping' | 'scan' | null>(null);
  const [adjusting, setAdjusting] = useState<Supply | null>(null);
  const summary = useInventorySummary();
  const filaments = useFilaments();
  const spools = useSpools();
  const supplies = useSupplies();
  if (!phone) return <Navigate to="/filamentos" replace />;
  const s = summary.data;
  const lowCount = (s?.lowFilaments ?? 0) + (s?.lowSupplies ?? 0);
  const order: SpoolStatus[] = ['loaded', 'opened', 'sealed', 'empty'];
  const spoolRows = (spools.data?.data ?? [])
    .filter((x) => !status || x.status === status)
    .toSorted((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
  const error = filaments.isError || spools.isError || supplies.isError;

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8 }}>
        <div className="grow">
          <h1 style={{ fontSize: 22 }}>Estoque</h1>
          <p className="sm" style={{ color: lowCount ? 'var(--warn)' : 'var(--ink2)' }}>
            {s
              ? lowCount
                ? `${s.lowFilaments} ${s.lowFilaments === 1 ? 'filamento' : 'filamentos'} e ${s.lowSupplies} ${s.lowSupplies === 1 ? 'insumo' : 'insumos'} abaixo do mínimo`
                : 'Tudo acima do mínimo'
              : ' '}
          </p>
        </div>
        <button
          type="button"
          className="btn icon"
          aria-label="Ler etiqueta (QR)"
          onClick={() => setPanel('scan')}
        >
          <Icon name="search" />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button type="button" className="btn" onClick={() => setPanel('shopping')}>
          Lista de compras{lowCount ? ` · ${lowCount}` : ''}
        </button>
        <Link className="btn p" to="/filamentos/compra">
          Registrar compra
        </Link>
      </div>
      <div className="seg" role="tablist" aria-label="Estoque">
        {(
          [
            ['filamentos', 'Filamentos'],
            ['carreteis', 'Carretéis'],
            ['insumos', 'Insumos'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={tab === k ? 'on' : ''}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>
      {error ? (
        <ErrorBox
          title="Não foi possível carregar o estoque"
          detail="Sem conexão. Os carretéis e insumos continuam salvos."
          onRetry={() => {
            void filaments.refetch();
            void spools.refetch();
            void supplies.refetch();
          }}
        />
      ) : null}

      {tab === 'filamentos' && filaments.data ? (
        filaments.data.data.length === 0 ? (
          <div className="card">
            <EmptyState title="Nenhum filamento ainda">
              Registre a primeira compra: cada carretel ganha código e etiqueta.
            </EmptyState>
          </div>
        ) : (
          <div className="card col" style={{ gap: 0, padding: '4px 14px' }}>
            {filaments.data.data.map((f) => {
              const min = f.minimumGrams ?? 0;
              const pct = min ? Math.min(100, (f.stockGrams / (min * 2)) * 100) : 100;
              return (
                <button
                  key={f.id}
                  type="button"
                  className="col"
                  aria-label={`Ver ${f.name}`}
                  onClick={() => setOpenFilament(f)}
                  style={{
                    gap: 6,
                    padding: '12px 0',
                    background: 'none',
                    border: 0,
                    borderBottom: '1px solid var(--line)',
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div className="row" style={{ gap: 10 }}>
                    <Swatch hex={f.colorHex} size={18} />
                    <span className="grow trunc" style={{ fontWeight: 500 }}>
                      {f.name}
                    </span>
                    <span className="tab" style={{ color: f.low ? 'var(--warn)' : undefined }}>
                      {formatG(f.stockGrams)}
                    </span>
                  </div>
                  <span className="xs muted">
                    {f.material} · {f.spoolCount} {f.spoolCount === 1 ? 'carretel' : 'carretéis'} ·{' '}
                    {min ? `mín. ${formatG(min)}` : 'sem mínimo'}
                  </span>
                  {min ? (
                    <div
                      style={{
                        position: 'relative',
                        height: 6,
                        borderRadius: 3,
                        background: 'var(--sunk)',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: `${pct}%`,
                          borderRadius: 3,
                          background: f.low ? 'var(--warn)' : 'var(--pe)',
                        }}
                      />
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: -3,
                          bottom: -3,
                          width: 2,
                          background: 'var(--ink2)',
                        }}
                      />
                    </div>
                  ) : null}
                </button>
              );
            })}
            <p className="xs muted" style={{ padding: '10px 0' }}>
              A barra vai até o dobro do mínimo; o traço marca o mínimo.
            </p>
          </div>
        )
      ) : null}

      {openFilament ? (
        <FilamentSheet f={openFilament} onClose={() => setOpenFilament(null)} />
      ) : null}

      {tab === 'carreteis' && spools.data ? (
        <>
          <div className="row wrap" style={{ gap: 6 }}>
            <button
              type="button"
              className={status === '' ? 'chip on' : 'chip'}
              onClick={() => setStatus('')}
            >
              Todos
            </button>
            {SpoolStatus.options.map((st) => (
              <button
                key={st}
                type="button"
                className={status === st ? 'chip on' : 'chip'}
                onClick={() => setStatus(st)}
              >
                {SPOOL_STATUS_LABELS[st]}
              </button>
            ))}
          </div>
          <div className="card col" style={{ gap: 0, padding: '4px 14px' }}>
            {spoolRows.map((sp) => (
              <Link
                key={sp.id}
                to={`/carreteis/${sp.id}`}
                className="row"
                style={{ gap: 10, padding: '12px 0', borderBottom: '1px solid var(--line)' }}
              >
                <Swatch hex={sp.filament.colorHex} size={18} />
                <span className="grow" style={{ minWidth: 0 }}>
                  <span className="trunc" style={{ fontWeight: 500, display: 'block' }}>
                    {sp.code} · {sp.filament.name}
                  </span>
                  <span className="xs muted">
                    {sp.status === 'empty' ? 'pode descartar' : spoolPlace(sp)}
                  </span>
                </span>
                <span className="col" style={{ alignItems: 'flex-end', gap: 4 }}>
                  <span className="tab" style={{ color: sp.low ? 'var(--warn)' : undefined }}>
                    {sp.status === 'empty' ? 'vazio' : formatG(sp.remainingGrams)}
                  </span>
                  <SpoolBadge spool={sp} />
                </span>
              </Link>
            ))}
            {spoolRows.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>
                <p>
                  {spools.data.data.length
                    ? 'Nenhum carretel nessa situação'
                    : 'Nenhum carretel ainda'}
                </p>
                {status ? (
                  <button type="button" className="btn sm" onClick={() => setStatus('')}>
                    Limpar filtro
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {tab === 'insumos' && supplies.data ? (
        supplies.data.data.length === 0 ? (
          <div className="card">
            <EmptyState title="Nenhum insumo ainda">
              Cadastre embalagens e ferragens no computador para o custo do envio sair certo.
            </EmptyState>
          </div>
        ) : (
          <div className="card col" style={{ gap: 0, padding: '4px 14px' }}>
            <p className="xs muted" style={{ padding: '8px 0' }}>
              {supplies.data.data.length} insumos · toque para ajustar a quantidade
            </p>
            {supplies.data.data.map((x) => (
              <button
                key={x.id}
                type="button"
                className="listrow"
                style={{ width: '100%', borderBottom: '1px solid var(--line)' }}
                onClick={() => setAdjusting(x)}
              >
                <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                  <span className="trunc" style={{ fontWeight: 500, display: 'block' }}>
                    {x.name}
                  </span>
                  <span className="xs muted">
                    {SUPPLY_CATEGORY_LABELS[x.category]}
                    {x.minimumQuantity ? ` · mínimo ${qty(x.minimumQuantity)}` : ''}
                  </span>
                </span>
                <span className="col" style={{ alignItems: 'flex-end' }}>
                  <span className="tab" style={{ color: x.low ? 'var(--warn)' : undefined }}>
                    {qty(x.quantityOnHand)} {SUPPLY_UNIT_LABELS[x.unit].short}
                  </span>
                  {x.low ? (
                    <span className="xs" style={{ color: 'var(--warn)' }}>
                      abaixo do mínimo
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
            <Link to="/insumos" className="xs" style={{ padding: '10px 0' }}>
              Kits de embalagem e custos · no computador
            </Link>
          </div>
        )
      ) : null}

      {!filaments.data && !error ? <Skeleton rows={3} height={56} /> : null}
      {panel === 'shopping' ? <ShoppingPanel onClose={() => setPanel(null)} /> : null}
      {panel === 'scan' ? <QrScanSheet onClose={() => setPanel(null)} /> : null}
      {adjusting ? (
        <AdjustSupplyPanel supply={adjusting} onClose={() => setAdjusting(null)} />
      ) : null}
    </div>
  );
}
